import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import unsplash from "../api/unsplash";
import useConsulenzaData, { consulenzaAuth } from "./useConsulenzaData";
export default function UsciteTecnici({ clienteId }) {
  const [mese, setMese] = useState(moment().format("YYYY-MM"));
  const list = useConsulenzaData("usciteTecnici", clienteId, mese);
  const [form, setForm] = useState({
    Sede: "",
    Data: moment().format("YYYY-MM-DD"),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [remove, setRemove] = useState(null);
  useEffect(() => {
    if (list.data?.sedi.length === 1)
      setForm((p) => (p.Sede ? p : { ...p, Sede: list.data.sedi[0].ID }));
  }, [list.data]);
  const reset = () =>
    setForm({
      Sede: list.data?.sedi.length === 1 ? list.data.sedi[0].ID : "",
      Data: moment().format("YYYY-MM-DD"),
    });
  const save = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await unsplash[form.ID ? "put" : "post"](
        "usciteTecnici",
        { cliente: clienteId, id: form.ID, uscita: form },
        consulenzaAuth(),
      );
      setMessage(
        "Uscita registrata. Il rimborso è gestito nella fatturazione consulenza.",
      );
      if (form.Data.slice(0, 7) !== mese) setMese(form.Data.slice(0, 7));
      else await list.reload();
      reset();
    } catch (e) {
      setError(e.response?.data?.message || "Impossibile registrare l’uscita");
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await unsplash.delete("usciteTecnici", {
        ...consulenzaAuth(),
        params: { cliente: clienteId, id: remove.ID },
      });
      if (form.ID === remove.ID) reset();
      setRemove(null);
      await list.reload();
      setMessage("Uscita annullata ed esclusa dal riepilogo.");
    } catch (e) {
      setError(e.response?.data?.message || "Impossibile annullare l’uscita");
      setRemove(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Uscite tecnici</Typography>
      <Typography variant="body2" color="text.secondary">
        Registra la sede e il giorno della visita. Il tecnico è il tuo utente di
        accesso. Qui trovi soltanto le tue uscite. Il rimborso viene calcolato
        con le condizioni salvate per il cliente e la sede; tutte le uscite
        confluiscono nel riepilogo mensile di “Fatt. Consulenza”.
      </Typography>
      {(error || list.error) && (
        <Alert severity="error">{error || list.error}</Alert>
      )}
      {message && <Alert severity="success">{message}</Alert>}
      {list.data?.cessato && (
        <Alert severity="warning">
          Cliente cessato: nuove uscite disabilitate. Puoi correggere o
          annullare le tue registrazioni precedenti.
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
          p: 2,
          bgcolor: "#f5f8fa",
          borderRadius: 2,
        }}
      >
        <TextField
          size="small"
          select
          label="Sede visitata"
          value={form.Sede}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 230, maxWidth: "100%" }}
          disabled={busy}
          onChange={(e) => setForm((p) => ({ ...p, Sede: e.target.value }))}
        >
          <option value="">Seleziona sede</option>
          {form.ID &&
            !list.data?.sedi.some(
              (s) => Number(s.ID) === Number(form.Sede),
            ) && (
              <option value={form.Sede}>
                {form.SedeDescrizione} (cessata)
              </option>
            )}
          {list.data?.sedi.map((s) => (
            <option key={s.ID} value={s.ID}>
              {s.Descrizione || `Sede ${s.ID}`}
            </option>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label="Data uscita"
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: moment().format("YYYY-MM-DD") }}
          value={form.Data}
          disabled={busy}
          onChange={(e) => setForm((p) => ({ ...p, Data: e.target.value }))}
        />
        <Button
          variant="contained"
          disabled={
            busy ||
            !form.Sede ||
            !form.Data ||
            (!form.ID && (!list.data || list.data.cessato))
          }
          onClick={save}
        >
          {form.ID ? "Salva modifica" : "Registra uscita"}
        </Button>
        {form.ID && (
          <Button disabled={busy} onClick={reset}>
            Annulla modifica
          </Button>
        )}
      </Box>
      {list.data && !list.data.sedi.length && !list.data.cessato && (
        <Alert severity="info">
          Per registrare un’uscita occorre prima aggiungere una sede al cliente.
        </Alert>
      )}
      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <Typography variant="subtitle1">Le mie uscite</Typography>
        <TextField
          size="small"
          type="month"
          label="Mese"
          InputLabelProps={{ shrink: true }}
          value={mese}
          onChange={(e) => e.target.value && setMese(e.target.value)}
        />
        <Button
          size="small"
          disabled={list.loading || busy}
          onClick={list.reload}
        >
          Aggiorna
        </Button>
      </Box>
      {list.loading && (
        <CircularProgress size={20} aria-label="Caricamento uscite" />
      )}
      {list.data &&
        (!list.data.uscite.length ? (
          <Typography color="text.secondary">
            Nessuna tua uscita nel mese selezionato.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Sede</TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.data.uscite.map((r) => (
                  <TableRow key={r.ID}>
                    <TableCell>{moment(r.Data).format("DD/MM/YYYY")}</TableCell>
                    <TableCell>{r.SedeDescrizione}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          setForm(r);
                          setError("");
                          setMessage("");
                        }}
                      >
                        Modifica
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={busy}
                        onClick={() => setRemove(r)}
                      >
                        Annulla uscita
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
      <Dialog open={!!remove} onClose={() => !busy && setRemove(null)}>
        <DialogTitle>Annulla uscita</DialogTitle>
        <DialogContent>
          L’uscita del {remove && moment(remove.Data).format("DD/MM/YYYY")}{" "}
          presso {remove?.SedeDescrizione} verrà esclusa dal riepilogo dei
          rimborsi.
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setRemove(null)}>
            Mantieni
          </Button>
          <Button color="error" disabled={busy} onClick={cancel}>
            Conferma annullamento
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
